import type { i18n as Ii18, TOptions } from 'i18next';
import { flow, makeObservable } from 'mobx';

import { requireService } from 'src/packages/di';
import { fetchSettingsLabels, fetchValuesInterpretations } from 'src/packages/directories/api/directories.api';

import { OperationAbortedError, ValidationTextApiError } from '../errors';

export interface Ii18Service {
  i18n: Ii18;
  t(key: string, options?: TOptions): string;
  init: (primary: string, secondary?: string) => void;
  addResourceBundle: (lang: string, namespace: string, dictionary: object, deep?: boolean, overwrite?: boolean) => void;
}

export class I18NextStore implements Ii18Service {
  readonly i18n: Ii18;

  constructor(
    i18: Ii18,
    private readonly notifications = requireService('notifications'),
  ) {
    this.i18n = i18;
    makeObservable(this);
  }

  addResourceBundle(lang: string, namespace: string, dictionary: object): void {
    this.i18n.addResourceBundle(lang, namespace, dictionary);
  }

  private async loadLabelsAndInterpretations(lang: string): Promise<void> {
    await Promise.all([this.loadLables(lang), this.loadValuesInterpretations(lang)]);
  }

  t(key: string, options?: TOptions): string {
    const value = this.i18n.t(key, options);

    return value;
  }

  @flow.bound
  private async *loadValuesInterpretations(lang: string) {
    try {
      const interpretations = await fetchValuesInterpretations(lang);

      yield;

      this.addResourceBundle(lang, 'Interpretations', interpretations);
    } catch (error) {
      yield;
      if (error instanceof OperationAbortedError) return;

      if (error instanceof ValidationTextApiError && error.responseMessage) {
        this.notifications.showErrorMessage(error.responseMessage);
        return;
      }

      console.error(error);
      this.notifications.showErrorMessageT('errors:receivingInterpretations');
    }
  }

  @flow.bound
  private async *loadLables(lang: string) {
    try {
      const labels = await fetchSettingsLabels(lang);

      yield;

      this.addResourceBundle(lang, 'Labels', labels);
    } catch (error) {
      if (error instanceof OperationAbortedError) return;

      if (error instanceof ValidationTextApiError && error.responseMessage) {
        this.notifications.showErrorMessage(error.responseMessage);
        return;
      }

      console.error(error);
      this.notifications.showErrorMessageT('errors:receivingLabels');
    }
  }

  loadLabelsAndInterpretationsLangs = async (primary: string, secondary?: string) => {
    try {
      const languages = [primary, secondary].filter((lang): lang is string => !!lang);
      const promises = languages.map((lang) => this.loadLabelsAndInterpretations(lang));

      await Promise.all(promises);
    } catch (error) {
      console.error(error);
    }
  };

  init = async (primary: string, secondary?: string) => {
    this.loadLabelsAndInterpretationsLangs(primary, secondary);
  };
}
